import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  useFoodicsStatus,
  useDisconnectFoodics,
} from "src/api/integrations/foodics";
import { getFoodicsInstallUrl } from "./constants";
import { getLocalStoredData } from "src/utils/_localStorage";
import { ConstLocalStorage } from "src/constants/_localStorage";
import { notificationsState } from "src/store/notifications";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import foodicsLogo from "src/assets/svg-icons/foodics.svg";

const FoodicsSettings = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const { data: status, isLoading, refetch } = useFoodicsStatus();
  const { mutate: disconnect, status: disconnectStatus } =
    useDisconnectFoodics();

  const isConnected = status?.connected && status?.is_active;

  const handleConnect = () => {
    const user = getLocalStoredData(ConstLocalStorage.user);
    const companyId = Number(user?.company?.id ?? user?.company_id ?? 0);
    if (!companyId) {
      return;
    }
    const url = getFoodicsInstallUrl(companyId);
    if (!url) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: generalT("apiBaseUrlMissing"),
        },
      ]);
      return;
    }
    window.open(url, "_blank");
    setIsPolling(true);
  };

  useEffect(() => {
    if (isPolling && !isConnected) {
      pollingRef.current = setInterval(() => {
        refetch();
      }, 2500);
    }

    if (isConnected && isPolling) {
      setIsPolling(false);
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("callback.success"),
        },
      ]);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isPolling, isConnected, refetch]);

  const handleDisconnect = () => {
    disconnect();
  };

  useEffect(() => {
    if (disconnectStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("callback.success"),
        },
      ]);
      setDisconnectDialogOpen(false);
      refetch();
    } else if (disconnectStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("errors.disconnectFailed"),
        },
      ]);
    }
  }, [disconnectStatus]);

  if (isLoading) {
    return (
      <Stack
        gap={3}
        p={{ xs: 2, sm: "8px 32px 32px 32px" }}
        alignItems="center"
        justifyContent="center"
        minHeight={400}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/integrations")}
            sx={{ color: "#667085" }}
          >
            {generalT("back")}
          </Button>
          <Box
            component="img"
            src={foodicsLogo}
            alt="Foodics"
            width={48}
            height={48}
            sx={{ objectFit: "contain" }}
          />
          <Box>
            <Typography fontSize={24} fontWeight={600} color="#101828">
              {t("foodics.title")}
            </Typography>
            <Chip
              size="small"
              label={
                isConnected
                  ? t("status.connected")
                  : t("status.disconnected")
              }
              color={isConnected ? "success" : "default"}
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Stack>
        {isConnected && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<LinkOffIcon />}
            onClick={() => setDisconnectDialogOpen(true)}
          >
            {t("actions.disconnect")}
          </Button>
        )}
      </Box>

      {/* Divider */}
      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {/* Content */}
      {isConnected ? (
        <Stack spacing={3}>
          {/* Connection Info Card */}
          <Card variant="outlined" sx={{ borderRadius: "12px" }}>
            <CardContent>
              <Typography
                fontSize={16}
                fontWeight={600}
                color="#101828"
                mb={2}
              >
                {t("foodics.title")} {t("status.connected")}
              </Typography>
              <Stack spacing={1.5}>
                {status?.business_name && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontSize={14} color="#667085">
                      {t("foodics.businessName")}
                    </Typography>
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color="#344054"
                    >
                      {status.business_name}
                    </Typography>
                  </Box>
                )}
                {status?.connected_at && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontSize={14} color="#667085">
                      {t("foodics.connectedAt")}
                    </Typography>
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color="#344054"
                    >
                      {new Date(status.connected_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14} color="#667085">
                    {t("status.active")}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      status?.is_active
                        ? t("status.active")
                        : t("status.inactive")
                    }
                    color={status?.is_active ? "success" : "default"}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Typography fontSize={16} fontWeight={600} color="#101828">
            {t("foodicsHub.sectionTitle")}
          </Typography>
          <Box
            display="grid"
            gap={2}
            sx={{
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                  borderColor: "#FF8D3F",
                },
              }}
              onClick={() => navigate("/integrations/foodics/menu")}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={40}
                    height={40}
                    bgcolor="#fbeada"
                    borderRadius="8px"
                  >
                    <RestaurantMenuIcon sx={{ color: "#FF8D3F" }} />
                  </Box>
                  <Box>
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color="#101828"
                    >
                      {t("foodicsHub.cardMenuTitle")}
                    </Typography>
                    <Typography fontSize={12} color="#667085">
                      {t("foodicsHub.cardMenuSubtitle")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                  borderColor: "#FF8D3F",
                },
              }}
              onClick={() => navigate("/integrations/foodics/inventory")}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={40}
                    height={40}
                    bgcolor="#fbeada"
                    borderRadius="8px"
                  >
                    <Inventory2Icon sx={{ color: "#FF8D3F" }} />
                  </Box>
                  <Box>
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color="#101828"
                    >
                      {t("foodicsHub.cardInventoryTitle")}
                    </Typography>
                    <Typography fontSize={12} color="#667085">
                      {t("foodicsHub.cardInventorySubtitle")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                  borderColor: "#FF8D3F",
                },
              }}
              onClick={() => navigate("/integrations/foodics/orders")}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={40}
                    height={40}
                    bgcolor="#fbeada"
                    borderRadius="8px"
                  >
                    <ReceiptLongIcon sx={{ color: "#FF8D3F" }} />
                  </Box>
                  <Box>
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color="#101828"
                    >
                      {t("foodicsHub.cardJourneyTitle")}
                    </Typography>
                    <Typography fontSize={12} color="#667085">
                      {t("foodicsHub.cardJourneySubtitle")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                  borderColor: "#FF8D3F",
                },
              }}
              onClick={() => navigate("/integrations/foodics/mappings")}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={40}
                    height={40}
                    bgcolor="#fbeada"
                    borderRadius="8px"
                  >
                    <AccountTreeIcon sx={{ color: "#FF8D3F" }} />
                  </Box>
                  <Box>
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color="#101828"
                    >
                      {t("foodicsHub.cardBranchesTitle")}
                    </Typography>
                    <Typography fontSize={12} color="#667085">
                      {t("foodicsHub.cardBranchesSubtitle")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      ) : (
        /* Not Connected State */
        <Card variant="outlined" sx={{ borderRadius: "12px" }}>
          <CardContent>
            <Stack spacing={3} alignItems="center" py={4}>
              <Box
                component="img"
                src={foodicsLogo}
                alt="Foodics"
                width={80}
                height={80}
                sx={{ objectFit: "contain" }}
              />
              <Box textAlign="center">
                <Typography fontSize={18} fontWeight={600} color="#101828">
                  {t("foodics.title")}
                </Typography>
                <Typography
                  fontSize={14}
                  color="#667085"
                  mt={1}
                  maxWidth={400}
                >
                  {status?.install_uses_dev_token
                    ? t("foodics.connectPromptDev")
                    : t("foodics.connectPrompt")}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<LinkIcon />}
                onClick={handleConnect}
              >
                {t("actions.connect")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Disconnect Confirmation Dialog */}
      <ConfirmPopup
        open={disconnectDialogOpen}
        handleClose={() => setDisconnectDialogOpen(false)}
        handleConfirm={handleDisconnect}
        isPending={disconnectStatus === "pending"}
        title={t("foodics.disconnectConfirm")}
        subtitle={t("foodics.disconnectWarning")}
        confirmBtnText={t("actions.disconnect")}
      />
    </Stack>
  );
};

export default FoodicsSettings;
